import { Person } from "../../../services/interface/query/person";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import InfoOnline from "../../components/InfoOnline";
import NotPermissionComponent from "../NotPermissionComponent";
import { useFindOffendedDemands } from "../../../services/apis/apiLegalInformation";
import { DemandsInterface } from "../../../services/interface/query/legal-information/demands-interface";
import { ItemInfo, ItemInfoSpouse } from "../../components/ViewData";
import * as IconsMui from "@mui/icons-material";
import { formatDateString } from "../../../utils/daysOptions";
import CustomTable from "../../../components/general/CustomTable";
import NoRecords from "../../components/NoRecords";
import {
    Accordion,
    AccordionHeader,
    AccordionBody, Tooltip
} from "@material-tailwind/react";

interface Props {
    sri?: boolean;
    person?: Person;
}

export default function OffendedDemands({ sri, person }: Props) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: findOffendedDemands,
        data: successLegalInformation,
        error: errorLegalInformation,
        isPending: loadingLegalInformation,
    } = useFindOffendedDemands();

    const [response, setResponse] = useState<DemandsInterface[] | undefined>();
    const [openAccordion, setOpenAccordion] = useState<number | null>(null);

    const handleOpen = (index: number) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const findLegal = () => {
        if (person && person.identification && !sri) {
            setResponse(undefined);
            findOffendedDemands({ identifier: person.identification, msj: t('criminal-record') });
        } else if (sri && person && person?.taxIdentificationNumber) {
            setResponse(undefined);
            findOffendedDemands({ identifier: person?.taxIdentificationNumber, msj: t('criminal-record') });
        }
    };

    useEffect(() => {
        findLegal();
    }, [person]);

    useEffect(() => {
        if (successLegalInformation) {
            setResponse(successLegalInformation);
            if (successLegalInformation.length > 0) {
                setOpenAccordion(0);
            }
        }
    }, [successLegalInformation]);

    const renderTooltipText = (text: string) => {
        const maxLength = 60;
        const shortText = text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

        return (
            <Tooltip
                content={
                    <div className="max-w-xs text-sm text-gray-800 p-2">
                        {text}
                    </div>
                }
                className="bg-white text-gray-800 border border-gray-300 shadow-md p-2 rounded-md z-50"
                placement="top"
            >
                <div className="truncate max-w-[200px] cursor-help">
                    {shortText}
                </div>
            </Tooltip>
        );
    };


    return (
        <div className="flex flex-col gap-3">
            <h2 className="text-center text-lg font-semibold text-gray-900 mb-6 w-full">
                {t("demands")}
            </h2>

            <div className="flex flex-row gap-2">
                <h3>{t("offended-demands")}</h3>
                <InfoOnline onClick={findLegal} />
            </div>

            <NotPermissionComponent
                title={"/legal_cases/demands"}
                loading={loadingLegalInformation}
                error={errorLegalInformation}
                component={
                    <>
                        {
                            !loadingLegalInformation && response && response.length > 0 ? (
                                response.map((item, index) => (
                                    <Accordion
                                        key={`accordion-demand-${index}`}
                                        open={openAccordion === index}
                                        icon={<IconsMui.ExpandMore />}
                                        placeholder=""
                                    >
                                    <AccordionHeader onClick={() => handleOpen(index)} placeholder="" >
                                        <div className="text-base">
                                        {`${t('demand-offended')} #${index + 1}`}
                                        </div>
                                        </AccordionHeader>
                                        <AccordionBody>
                                            <div className="flex flex-col gap-3 text-sm">
                                                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                                                    <ItemInfo
                                                        label={t("nro-process")}
                                                        text={item?.processNumber}
                                                        icon={<IconsMui.ReceiptLongTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t("date-hours")}
                                                        text={formatDateString(item?.dateRegister)}
                                                        icon={<IconsMui.EventTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t("crime-process")}
                                                        text={item?.nameCase}
                                                        icon={<IconsMui.AccountBalanceTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                                                    <ItemInfo
                                                        label={t("name-case")}
                                                        text={item?.nameCase}
                                                        icon={<IconsMui.ReceiptLongTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t("legal-scope")}
                                                        text={item?.legalScope}
                                                        icon={<IconsMui.ReceiptLongTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                    <ItemInfo
                                                        label={t("type-actions")}
                                                        text={item?.typeAction}
                                                        icon={<IconsMui.ReceiptLongTwoTone />}
                                                        classNameValue="text-sm"
                                                    />
                                                </div>
                                            </div>

                                            {
                                                item.actions && item?.actions.length > 0 && (
                                                    <>
                                                        <div className="capitalize text-empColorPrimary font-semibold text-[14px]">
                                                            {`${t("detail")} ${t("demand")}`}
                                                        </div>
                                                        <CustomTable
                                                            key={`table-complaints`}
                                                            header={[
                                                                t("date"),
                                                                t("unit"),
                                                                t("actor-offended"),
                                                                t("actor-process")
                                                            ]}
                                                            listElement={
                                                                item.actions?.map((action) => {
                                                                    const date = action.date || "-";
                                                                    const unit = action.unit || "-";

                                                                    const actorList = action.actor?.map(a =>
                                                                        `${a.names || "-"} (${a.identification || "-"})`
                                                                    ) || [];

                                                                    const defendantList = action.defendant?.map(d =>
                                                                        `${d.names || "-"} (${d.identification || "-"})`
                                                                    ) || [];

                                                                    const joinAndTruncate = (list: string[]) =>
                                                                        list.join(", ").slice(0, 150) + (list.join(", ").length > 150 ? "..." : "");

                                                                    return {
                                                                        row: [
                                                                            date,
                                                                            renderTooltipText(unit),
                                                                            renderTooltipText(joinAndTruncate(actorList)),
                                                                            renderTooltipText(joinAndTruncate(defendantList)),
                                                                        ]
                                                                    };
                                                                }) || []
                                                            }
                                                        />
                                                    </>
                                                )
                                            }

                                        </AccordionBody>
                                    </Accordion>
                                ))
                            ) : <NoRecords />
                        }
                    </>
                }
            />
        </div>
    );
}
